import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, Clock, Mail } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { StoryInvite } from "@/components/HomeSections";
import { useSeo } from "@/hooks/useSeo";
import { GUIDA_FAQ, GUIDA_SEZIONI } from "@/data/seo";
import { COMMUNITY_TABLE, MENTORSHIP_PAIR } from "@/data/media";

const IMMAGINI = {
  passi: {
    src: COMMUNITY_TABLE,
    alt: "Donne imprenditrici al lavoro intorno a un tavolo",
  },
  rete: {
    src: MENTORSHIP_PAIR,
    alt: "Imprenditrice esperta che affianca una donna che sta avviando la sua impresa",
  },
};

const fade = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function GuidaPage() {
  useSeo({ path: "/donna-impresa" });

  return (
    <PageShell testId="guida-page">
      <article className="mx-auto max-w-[1400px] px-5 sm:px-10">
        <header className="pt-10 sm:pt-14">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
            Guida · Donne in impresa
          </p>
          <h1
            className="mt-5 max-w-4xl font-display text-[2.4rem] leading-[1.03] tracking-tight text-[#1A1617] sm:text-5xl lg:text-6xl"
            data-testid="guida-title"
          >
            Donna Impresa: la guida alle donne
            <br className="hidden sm:block" />{" "}
            <span className="di-shine italic">che fanno impresa</span>.
          </h1>
          <p
            className="mt-7 max-w-3xl text-base leading-relaxed text-[#6B5E62]"
            data-testid="guida-lead"
          >
            Che cosa significa oggi essere una donna in impresa in Italia, come
            si apre un'attività passo per passo, quali agevolazioni esistono per
            le donne imprenditrici, quali ostacoli si incontrano davvero e
            perché una rete cambia il risultato. Una guida pratica, senza
            promesse e senza gergo.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#9B8F92]">
            <span className="inline-flex items-center gap-2">
              <Clock size={13} className="text-[#E0708A]" /> Lettura: circa 10
              minuti
            </span>
            <span>A cura della redazione DonnaImpresa</span>
          </div>
        </header>

        <nav
          className="mt-12 rounded-[28px] border border-[#E8E2E4] bg-white p-7 sm:p-9"
          aria-label="Indice della guida"
          data-testid="guida-indice"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5E2A52]">
            In questa guida
          </p>
          <ol className="mt-5 grid gap-x-10 gap-y-3 sm:grid-cols-2">
            {GUIDA_SEZIONI.map((s, i) => (
              <li key={s.id} className="flex gap-3 text-sm">
                <span className="font-display text-[#E0708A]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <a
                  href={`#${s.id}`}
                  data-testid={`guida-indice-link-${s.id}`}
                  className="di-underline text-[#6B5E62] transition-colors duration-300 hover:text-[#1A1617]"
                >
                  {s.titolo}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-16 grid gap-16 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-14">
          <div>
            {GUIDA_SEZIONI.map((s, i) => (
              <motion.section
                key={s.id}
                id={s.id}
                {...fade}
                className="scroll-mt-32 border-t border-[#E8E2E4] pt-12 first:border-0 first:pt-0"
                data-testid={`guida-sezione-${s.id}`}
              >
                <p className="font-display text-sm text-[#E0708A]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="mt-3 max-w-3xl font-display text-[1.75rem] leading-tight tracking-tight text-[#1A1617] sm:text-4xl">
                  {s.titolo}
                </h2>
                <div className="mt-6 space-y-5">
                  {s.paragrafi.map((p) => (
                    <p
                      key={p.slice(0, 40)}
                      className="max-w-3xl text-base leading-[1.8] text-[#5B5054]"
                    >
                      {p}
                    </p>
                  ))}
                </div>

                {s.punti && (
                  <ul className="mt-9 space-y-4" data-testid={`guida-punti-${s.id}`}>
                    {s.punti.map((p) => (
                      <li
                        key={p.slice(0, 30)}
                        className="flex max-w-3xl gap-4 rounded-2xl border border-[#E8E2E4] bg-white p-5"
                      >
                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#5E2A52]">
                          <Check size={13} className="text-white" />
                        </span>
                        <p className="text-sm leading-relaxed text-[#5B5054]">{p}</p>
                      </li>
                    ))}
                  </ul>
                )}

                {IMMAGINI[s.id] && (
                  <figure className="mt-10 overflow-hidden rounded-[28px]">
                    <img
                      src={IMMAGINI[s.id].src}
                      alt={IMMAGINI[s.id].alt}
                      loading="lazy"
                      className="h-[260px] w-full object-cover object-[center_28%] sm:h-[340px]"
                      data-testid={`guida-immagine-${s.id}`}
                    />
                    <figcaption className="mt-3 text-xs text-[#9B8F92]">
                      {IMMAGINI[s.id].alt}.
                    </figcaption>
                  </figure>
                )}
              </motion.section>
            ))}
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-36 rounded-[28px] bg-[#5E2A52] p-7 text-white">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                Fai parte della rete
              </p>
              <p className="mt-4 font-display text-2xl leading-tight">
                La tua storia d'impresa vale più di una pubblicità.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-white/70">
                Due minuti, gratuito, pubblichiamo solo con il tuo ok.
              </p>
              <Link
                to="/racconta-la-tua-storia"
                data-testid="guida-aside-cta"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#5E2A52] transition-colors duration-300 hover:bg-[#E0708A] hover:text-white"
              >
                Racconta la tua storia
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
              <a
                href="mailto:impresahub@gmail.com"
                data-testid="guida-aside-email"
                className="mt-6 flex items-center gap-2 text-xs text-white/60 transition-colors duration-300 hover:text-white"
              >
                <Mail size={14} /> impresahub@gmail.com
              </a>
            </div>
          </aside>
        </div>

        <section
          className="mt-20 border-t border-[#E8E2E4] pt-14"
          aria-label="Domande frequenti su donna impresa"
          data-testid="guida-faq"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
            Domande frequenti
          </p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl leading-tight tracking-tight text-[#1A1617] sm:text-4xl">
            Le domande che ci fanno più spesso
          </h2>
          <div className="mt-9 max-w-3xl divide-y divide-[#E8E2E4] border-y border-[#E8E2E4]">
            {GUIDA_FAQ.map((f, i) => (
              <details
                key={f.q}
                className="group py-5"
                data-testid={`guida-faq-item-${i}`}
              >
                <summary className="flex cursor-pointer list-none items-start justify-between gap-6 text-base font-medium text-[#1A1617] marker:content-none">
                  <h3 className="text-base font-medium">{f.q}</h3>
                  <ChevronDown
                    size={18}
                    className="mt-1 shrink-0 text-[#E0708A] transition-transform duration-300 group-open:rotate-180"
                  />
                </summary>
                <p className="mt-4 pr-10 text-sm leading-relaxed text-[#5B5054]">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section
          className="mt-16 rounded-[28px] border border-[#E8E2E4] bg-white p-7 sm:p-9"
          aria-label="Continua a leggere"
          data-testid="guida-link-interni"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5E2A52]">
            Continua
          </p>
          <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-sm">
            {[
              ["/manifesto", "Il manifesto della rete"],
              ["/community", "La community delle donne in impresa"],
              ["/racconta-la-tua-storia", "Racconta la tua storia"],
              ["/sponsor", "Diventa azienda sponsor"],
              ["/social", "Gli ultimi post su Instagram"],
            ].map(([href, label]) => (
              <Link
                key={href}
                to={href}
                data-testid={`guida-link-${href.replace(/\//g, "")}`}
                className="di-underline text-[#6B5E62] transition-colors duration-300 hover:text-[#E0708A]"
              >
                {label}
              </Link>
            ))}
          </div>
        </section>
      </article>

      <StoryInvite />
    </PageShell>
  );
}
